import { lc, tokens, uniq, makeNodeId, makeItemId } from "./helpers";
import type { ModuleNode } from "./types";
import type { NavNode, NavItem } from "./db";

export function transformModuleToRows(
    root: ModuleNode
): { nodes: NavNode[]; items: NavItem[] } {
    const moduleRootKey = root.key;
    const moduleRootId = lc(moduleRootKey);

    const nodes: NavNode[] = [];
    const items: NavItem[] = [];

    function walk(node: ModuleNode, pathKeys: string[], titles: string[]) {
        // current level is a "submodule" unless it is root (module) or page (handled inside)
        const isRoot = pathKeys.length === 1;
        const nodeType: "module" | "submodule" = isRoot ? "module" : "submodule";
        const nodeTitle = node.title || node.key;
        const nodeId = makeNodeId(pathKeys);
        const breadcrumb = [...titles, nodeTitle].join(" › ");

        // create NavNode for module/submodule itself
        const nodeTerms = uniq([
            ...tokens(nodeTitle),
            ...tokens(node.key),
            ...(node.route ? tokens(node.route) : []),
            ...tokens(breadcrumb),
        ]);

        const navNode: NavNode = {
            id: nodeId,
            type: nodeType,
            key: node.key,
            parentId: pathKeys.length > 1 ? makeNodeId(pathKeys.slice(0, -1)) : undefined,
            title: nodeTitle,
            route: node.route,
            moduleRootId,
            breadcrumb,
            path: pathKeys,
            terms: nodeTerms,
        };
        nodes.push(navNode);

        // pages at this level
        if (Array.isArray(node.pages)) {
            node.pages.forEach((page: any, idx: any) => {
                const pagePath = [...pathKeys, page.key];
                const pageId = makeNodeId(pagePath);
                const pageTitle = page.title || page.key;
                const pageBreadcrumb = [breadcrumb, pageTitle].join(" › ");
                const pageTerms = uniq([
                    ...tokens(pageTitle),
                    ...tokens(page.key),
                    ...tokens(page.route ?? ""),
                    ...tokens(pageBreadcrumb),
                ]);

                const pageNode: NavNode = {
                    id: pageId,
                    type: "page",
                    key: page.key,
                    parentId: nodeId,
                    title: pageTitle,
                    route: page.route,
                    moduleRootId,
                    order: idx,
                    breadcrumb: pageBreadcrumb,
                    path: pagePath,
                    terms: pageTerms,
                };
                nodes.push(pageNode);

                // page items → NavItem rows
                const arr = Array.isArray(page.items) ? page.items : [];
                arr.forEach((it: any, iidx: any) => {
                    const anchor = it.key;
                    const url = page.route ? `${page.route}#${encodeURIComponent(anchor)}` : "";
                    const itemBreadcrumb = `${pageBreadcrumb}`;
                    const itemTerms = uniq([
                        ...tokens(it.label || ""),
                        ...tokens(anchor),
                        ...(it.tags ?? []).flatMap(tokens),
                        ...tokens(page.route ?? ""),
                        ...tokens(itemBreadcrumb),
                    ]);

                    const itemRow: NavItem = {
                        id: makeItemId(pageId, anchor),
                        pageId,
                        key: anchor,
                        label: it.label || it.key,
                        tags: it.tags,
                        route: page.route ?? "",
                        url,
                        moduleRootId,
                        breadcrumb: itemBreadcrumb,
                        terms: itemTerms,
                        drawerType: it.drawerType,
                        drawerProps: it.drawerProps,
                    };
                    items.push(itemRow);
                });
            });
        }

        // submodules
        if (Array.isArray(node.submodules)) {
            node.submodules.forEach((sub: any) =>
                walk(sub, [...pathKeys, sub.key], [...titles, nodeTitle])
            );
        }
    }

    walk(root, [root.key], []);

    return { nodes, items };
}
